import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";

function getContainerRect(): DOMRect | null {
  const el = document.querySelector(".c_main__container");
  return el ? el.getBoundingClientRect() : null;
}

function getVisibleContainerStyle(rect: DOMRect | null): React.CSSProperties {
  if (!rect) return { position: "fixed", inset: 0 };

  // 현재 화면에 보이는 컨테이너 영역만 오버레이로 사용한다.
  const top = Math.max(rect.top, 0);
  const left = Math.max(rect.left, 0);
  const right = Math.min(rect.right, window.innerWidth);
  const bottom = Math.min(rect.bottom, window.innerHeight);

  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);

  if (width === 0 || height === 0) return { position: "fixed", inset: 0 };

  return {
    position: "fixed",
    top,
    left,
    width,
    height,
  };
}

interface ContainerLoadingOverlayProps {
  isOpen: boolean;
  title: React.ReactNode;
}

export default function ContainerLoadingOverlay({
  isOpen,
  title,
}: ContainerLoadingOverlayProps) {
  const [overlayRect, setOverlayRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const mainContainer = document.querySelector(
      ".c_main__container"
    ) as HTMLElement | null;
    if (!mainContainer) return;

    mainContainer.classList.add("overflow-hidden");

    const updateRect = () => setOverlayRect(getContainerRect());
    updateRect();

    mainContainer.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);

    return () => {
      mainContainer.classList.remove("overflow-hidden");
      mainContainer.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
      setOverlayRect(null);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const rect = overlayRect ?? getContainerRect();
  const overlayStyle = getVisibleContainerStyle(rect);

  const portalRoot =
    typeof document !== "undefined" ? document.querySelector("main") : null;
  if (!portalRoot) return null;

  return createPortal(
    <div className="z-[50] pointer-events-auto" style={overlayStyle}>
      <style>
        {`
          @keyframes roster-overlay-enter {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes roster-overlay-card {
            from { opacity: 0; transform: translateY(6px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}
      </style>
      <div
        className="absolute inset-0 bg-white/65 backdrop-blur-[1px]"
        style={{ animation: "roster-overlay-enter 140ms ease-out" }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div style={{ animation: "roster-overlay-card 160ms ease-out" }}>
          <Alert variant="default" className="border-sig/20 shadow-2xl">
            <Loader2 className="h-4 w-4 animate-spin text-sig" />
            <AlertTitle className="text-[13px] font-semibold">
              {title}
            </AlertTitle>
            <AlertDescription className="text-xs!">
              잠시만 기다려주세요.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </div>,
    portalRoot
  );
}
